import { PayoutTransactionClientData } from '@/types/Payouts';
import { Box } from '@palmetto/palmetto-components';
import { useMemo } from 'react';

import {
  PayeePayoutEvent,
  PayeePayoutEventMap,
  PayoutEvent,
  PayoutEventMap,
  PayoutEventToEventTypeMap,
  PayoutTransactionLevel,
} from 'types';

interface TransactionEventCellProps {
  transaction: PayoutTransactionClientData;
}

const flatLabelMap = Object.values(PayoutEventMap).flat();

/**
 * Both transaction levels store their event in the same `event` field, but PayoutEventMap is keyed
 * by program type — which a payee row does not have. Resolving a payee event through it fails the
 * eventType check below and lands on PayoutEvent.other, so every payee row would read "Other".
 * Hence the branch on level rather than one lookup for both.
 */
const getEventLabel = ({ event, eventType, transactionLevel }: PayoutTransactionClientData) => {
  if (transactionLevel === PayoutTransactionLevel.payee) {
    return PayeePayoutEventMap[event as PayeePayoutEvent];
  }

  const payoutEvent =
    PayoutEventToEventTypeMap[event as PayoutEvent] === eventType ? (event as PayoutEvent) : PayoutEvent.other;
  return flatLabelMap.find((item) => item.event === payoutEvent)?.name;
};

/**
 * Renders the payout event and its description as one cell, the event above the description.
 * They used to be two adjacent columns.
 */
export const TransactionEventCell = ({ transaction }: TransactionEventCellProps) => {
  const label = useMemo(() => getEventLabel(transaction), [transaction]);

  return (
    <Box direction="column" childGap="2xs">
      {label ? <Box fontWeight="medium">{label}</Box> : null}
      {transaction.description ? (
        <Box color="body-secondary" fontWeight="regular">
          {transaction.description}
        </Box>
      ) : null}
    </Box>
  );
};
